/*
 * Greenvill Crafts — WebMCP tools (https://developer.chrome.com/docs/ai/webmcp)
 * Exposes a small set of storefront tools to in-browser AI agents via the Imperative API.
 * Feature-detected: does nothing in browsers without WebMCP.
 * Guidelines followed: one job per tool, names <= 30 chars, descriptions <= 500 chars,
 * outputs kept under ~1.5K chars, readOnlyHint on read tools, UI updated before resolving.
 */
(() => {
  'use strict';
  const getMC = () => document.modelContext || navigator.modelContext;
  const ready = (m) => !!(m && typeof m.registerTool === 'function');

  // The browser may attach modelContext after this script runs (e.g. page loaded in a background tab),
  // so wait for it briefly instead of giving up straight away.
  const whenAvailable = (cb) => {
    if (ready(getMC())) return cb(getMC());
    let tries = 0;
    const iv = setInterval(() => {
      if (ready(getMC())) { clearInterval(iv); cb(getMC()); }
      else if (++tries > 60) clearInterval(iv);
    }, 500);
    document.addEventListener('visibilitychange', function once() {
      if (ready(getMC())) { document.removeEventListener('visibilitychange', once); clearInterval(iv); cb(getMC()); }
    });
  };

  let started = false;
  whenAvailable((mc) => { if (started) return; started = true; init(mc); });

  function init(mc) {
  const cfgEl = document.getElementById('webmcp-config');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  const root = (cfg.routes && cfg.routes.root) || '/';
  const r = (p) => (root.replace(/\/$/, '') + p);
  const MAX = 1500;

  const money = (cents) => {
    try { return new Intl.NumberFormat(cfg.locale || 'en-GB', { style: 'currency', currency: cfg.currency || 'GBP' }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(2); }
  };
  const clip = (s, n) => { s = String(s || '').replace(/\s+/g, ' ').trim(); return s.length > n ? s.slice(0, n - 1) + '…' : s; };
  const stripHtml = (html) => { const d = document.createElement('div'); d.innerHTML = html || ''; return d.textContent || ''; };
  // Keep every tool output inside the recommended character budget.
  const out = (obj) => {
    let s = JSON.stringify(obj);
    if (s.length <= MAX) return s;
    if (Array.isArray(obj.results)) { while (obj.results.length > 1 && JSON.stringify(obj).length > MAX) obj.results.pop(); obj.truncated = true; }
    if (Array.isArray(obj.items)) { while (obj.items.length > 1 && JSON.stringify(obj).length > MAX) obj.items.pop(); obj.truncated = true; }
    s = JSON.stringify(obj);
    return s.length <= MAX ? s : s.slice(0, MAX - 3) + '...';
  };
  const fail = (msg) => JSON.stringify({ ok: false, error: msg });
  const getJSON = async (url, signal) => {
    const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
    if (res.status === 429) throw new Error('The store is busy (rate limited). Wait a few seconds and try again.');
    if (!res.ok) throw new Error('Request failed with status ' + res.status);
    return res.json();
  };
  const handleFrom = (v) => {
    if (!v) return null;
    const m = String(v).match(/\/products\/([^/?#]+)/);
    return (m ? m[1] : String(v)).trim().toLowerCase();
  };
  // Ask the theme to re-render the cart drawer & count, and wait until it has done so.
  const refreshCart = (open) => new Promise((resolve) => {
    const done = () => { document.removeEventListener('theme:cart:refreshed', done); resolve(); };
    document.addEventListener('theme:cart:refreshed', done);
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: { open: !!open } }));
    setTimeout(done, 4000);
  });
  const summariseCart = (cart) => ({
    item_count: cart.item_count,
    subtotal: money(cart.total_price),
    items: cart.items.map((i) => ({ key: i.key, title: clip(i.product_title, 70), variant: i.variant_title || undefined, variant_id: i.variant_id, quantity: i.quantity, line_price: money(i.final_line_price) })),
    checkout_url: r('/checkout')
  });

  const tools = [
    {
      coveredBy: ['search_catalog'],
      name: 'search_products',
      description: 'Search the ' + cfg.shop + ' catalogue (woodturning tools, lathes, machinery, finishes, pen and ring making supplies, 40+ brands). Returns matching products with title, brand, price, stock status, handle and URL. Use the handle with get_product_details for variants and full details.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'What the shopper is looking for, e.g. "bowl gouge", "Record Power lathe", "friction polish".' },
          limit: { type: 'integer', minimum: 1, maximum: 10, description: 'Maximum number of results (default 5).' }
        },
        required: ['query']
      },
      annotations: { readOnlyHint: true },
      execute: async ({ query, limit }, { signal } = {}) => {
        if (!query || !String(query).trim()) return fail('Provide a search query.');
        const n = Math.min(Math.max(parseInt(limit, 10) || 5, 1), 10);
        const data = await getJSON(r('/search/suggest.json') + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + n + '&resources[options][unavailable_products]=last', signal);
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        return out({
          ok: true, query, count: products.length,
          results: products.map((p) => ({ title: clip(p.title, 80), brand: p.vendor, price: p.price ? money(Math.round(parseFloat(p.price) * 100)) : undefined, available: p.available, handle: p.handle, url: new URL(p.url, location.origin).pathname }))
        });
      }
    },
    {
      coveredBy: ['get_product'],
      name: 'get_product_details',
      description: 'Get details of one product: description summary, brand, price, stock and every purchasable variant (variant_id, option title, price, availability). Pass a product handle or URL. On a product page the handle can be omitted to use the product being viewed.',
      inputSchema: {
        type: 'object',
        properties: { product: { type: 'string', description: 'Product handle (e.g. "record-power-coronet-herald") or product URL. Optional on a product page.' } }
      },
      annotations: { readOnlyHint: true },
      execute: async ({ product } = {}, { signal } = {}) => {
        const handle = handleFrom(product) || cfg.product_handle;
        if (!handle) return fail('Provide a product handle or URL (use search_products to find one).');
        let p;
        try { p = await getJSON(r('/products/' + encodeURIComponent(handle) + '.js'), signal); }
        catch (e) { return fail('Product "' + handle + '" was not found. Use search_products to find the right handle.'); }
        return out({
          ok: true,
          title: p.title, brand: p.vendor, type: p.type || undefined, handle: p.handle,
          url: '/products/' + p.handle,
          price: p.price_varies ? 'from ' + money(p.price_min) : money(p.price),
          available: p.available,
          summary: clip(stripHtml(p.description), 500),
          variants: p.variants.slice(0, 20).map((v) => ({ variant_id: v.id, option: v.title === 'Default Title' ? undefined : v.title, sku: v.sku || undefined, price: money(v.price), available: v.available }))
        });
      }
    },
    {
      coveredBy: ['add_to_cart'],
      name: 'add_to_cart',
      description: 'Add a product variant to the shopper\'s cart and show the updated cart. Get the variant_id from get_product_details. Does not check out or take payment.',
      inputSchema: {
        type: 'object',
        properties: {
          variant_id: { type: 'integer', description: 'The variant_id to add, from get_product_details.' },
          quantity: { type: 'integer', minimum: 1, maximum: 99, description: 'How many to add (default 1).' }
        },
        required: ['variant_id']
      },
      execute: async ({ variant_id, quantity }, { signal } = {}) => {
        const id = parseInt(variant_id, 10);
        const qty = Math.min(Math.max(parseInt(quantity, 10) || 1, 1), 99);
        if (!id) return fail('variant_id must be a number from get_product_details.');
        const res = await fetch(r('/cart/add.js'), { method: 'POST', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: qty }] }) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return fail(data.description || data.message || 'Could not add this item (it may be sold out or the quantity unavailable).');
        await refreshCart(true);
        const cart = await getJSON(r('/cart.js'), signal);
        const added = (data.items && data.items[0]) || {};
        return out({ ok: true, added: { title: clip(added.product_title, 70), variant: added.variant_title || undefined, quantity: qty }, cart: summariseCart(cart) });
      }
    },
    {
      coveredBy: ['get_cart'],
      name: 'view_cart',
      description: 'Show what is in the shopper\'s cart: each line (with its key), quantities, line prices, subtotal and the checkout link.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: async (_args, { signal } = {}) => out({ ok: true, ...summariseCart(await getJSON(r('/cart.js'), signal)) })
    },
    {
      coveredBy: ['update_cart_lines'],
      name: 'update_cart_quantity',
      description: 'Change the quantity of a line already in the cart, or remove it by setting quantity to 0. Use the line key from view_cart.',
      inputSchema: {
        type: 'object',
        properties: {
          key: { type: 'string', description: 'The cart line key from view_cart.' },
          quantity: { type: 'integer', minimum: 0, maximum: 99, description: 'New quantity. 0 removes the line.' }
        },
        required: ['key', 'quantity']
      },
      execute: async ({ key, quantity }, { signal } = {}) => {
        const qty = parseInt(quantity, 10);
        if (!key || isNaN(qty) || qty < 0) return fail('Provide the line key from view_cart and a quantity of 0 or more.');
        const res = await fetch(r('/cart/change.js'), { method: 'POST', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: String(key), quantity: qty }) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return fail(data.description || data.message || 'That cart line was not found. Call view_cart to get current keys.');
        await refreshCart(false);
        return out({ ok: true, cart: summariseCart(data) });
      }
    },
    {
      name: 'get_store_info',
      description: 'Plan a visit to the ' + cfg.shop + ' showroom: address, opening hours, phone and email, plus links to the delivery, returns and privacy policies. Use when the shopper wants to visit, call or find the shop.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: async () => out({ ok: true, ...cfg.store })
    }
  ];

  // Wrap handlers so failures come back to the agent as readable errors.
  tools.forEach((tool) => {
    const run = tool.execute;
    tool.execute = async (args, ctx) => {
      try { return await run(args || {}, ctx || {}); }
      catch (e) { if (e && e.name === 'AbortError') throw e; return fail((e && e.message) || 'Something went wrong. Try again.'); }
    };
  });

  /* Shopify storefronts register their own WebMCP commerce tools (search_catalog, get_product,
     add_to_cart, get_cart, update_cart_lines, proceed_to_checkout ...). To avoid overlapping tools,
     ours are only registered when no equivalent exists, and only after Shopify's have loaded.
     get_store_info (showroom details) is always added. Tools are re-added if the registry is reset. */
  const toolDefinition = (t) => ({ name: t.name, description: t.description, inputSchema: t.inputSchema, execute: t.execute, annotations: t.annotations });
  const pending = new Set();
  let syncing = false;
  const sync = async () => {
    if (syncing) return;
    syncing = true;
    try {
      let names = [];
      try { names = (await mc.getTools()).map((t) => t.name); } catch (e) { /* getTools unsupported */ }
      for (const tool of tools) {
        if (names.includes(tool.name) || pending.has(tool.name)) continue;
        if (tool.coveredBy && tool.coveredBy.some((n) => names.includes(n))) continue;
        pending.add(tool.name);
        try { await mc.registerTool(toolDefinition(tool)); }
        catch (e) { if (!(e && e.name === 'InvalidStateError') && window.console) console.warn('[WebMCP] could not register', tool.name, e); }
        finally { pending.delete(tool.name); }
      }
    } finally { syncing = false; }
  };
  const afterLoad = () => new Promise((res) => {
    if (document.readyState === 'complete') res(); else window.addEventListener('load', res, { once: true });
  });
  // Shopify's own WebMCP script loads lazily and can reset the registry, so re-check a few times
  // after load (cheap: one getTools call) as well as whenever the tool list changes.
  afterLoad().then(() => {
    [1200, 3000, 6000, 10000, 20000, 40000].forEach((ms) => setTimeout(sync, ms));
    let t;
    if (typeof mc.addEventListener === 'function') mc.addEventListener('toolchange', () => { clearTimeout(t); t = setTimeout(sync, 600); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') sync(); });
  });

  /* Declarative API: annotate existing forms so agents can fill them in.
     No toolautosubmit — the shopper always reviews and presses the button themselves. */
  if (cfg.forms) {
    const annotate = (sel, name, desc, params) => {
      const form = document.querySelector(sel);
      if (!form || form.hasAttribute('toolname')) return;
      // Descriptions first, toolname last: Chrome registers the declarative tool when toolname appears.
      Object.keys(params).forEach((field) => {
        const el = form.querySelector(field);
        if (el) el.setAttribute('toolparamdescription', params[field]);
      });
      form.setAttribute('tooldescription', desc);
      form.setAttribute('toolname', name);
    };
    annotate('form[data-webmcp-form="newsletter"]', 'subscribe_to_newsletter',
      'Fill in the ' + cfg.shop + ' newsletter sign-up with the shopper\'s email address (new arrivals, show dates, course places and offers). The shopper confirms by pressing Subscribe.',
      { 'input[type="email"]': 'The shopper\'s email address.' });
    annotate('form[data-webmcp-form="contact"]', 'send_contact_message',
      'Fill in the ' + cfg.shop + ' contact form to ask the team a question (product advice, orders, courses, showroom visits). The shopper reviews and presses Send.',
      { '#ContactName': 'The shopper\'s name.', '#ContactEmail': 'The shopper\'s email address for the reply.', '#ContactPhone': 'Optional phone number.', '#ContactBody': 'The message or question for the team.' });
  }
  }
})();
